import React, { useMemo } from 'react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';

/**
 * 12-hour AM/PM time picker.
 * Stores value internally as 24-hour "HH:mm" string for backend compatibility.
 */
export const TimePicker12h = ({ value, onChange, className, 'data-testid': testId }) => {
  // Parse 24h value (e.g., "14:30") into 12h parts
  const { hour12, minute, period } = useMemo(() => {
    if (!value) return { hour12: '', minute: '', period: 'AM' };
    const [hStr, mStr] = value.split(':');
    let h = parseInt(hStr, 10);
    if (isNaN(h)) return { hour12: '', minute: '', period: 'AM' };
    const p = h >= 12 ? 'PM' : 'AM';
    h = h % 12;
    h = h === 0 ? 12 : h;
    return { hour12: String(h), minute: mStr || '00', period: p };
  }, [value]);

  // Convert 12h parts back to 24h "HH:mm"
  const update = (newH12, newMin, newPeriod) => {
    if (!newH12 || !newMin) return;
    let h = parseInt(newH12, 10);
    if (newPeriod === 'AM') {
      h = h === 12 ? 0 : h;
    } else {
      h = h === 12 ? 12 : h + 12;
    }
    const val = `${String(h).padStart(2, '0')}:${newMin}`;
    onChange(val);
  };

  const hours = Array.from({ length: 12 }, (_, i) => String(i + 1));
  const minutes = ['00', '15', '30', '45'];

  return (
    <div className={`flex items-center gap-1.5 ${className || ''}`} data-testid={testId}>
      <Select
        value={hour12}
        onValueChange={(h) => update(h, minute || '00', period)}
      >
        <SelectTrigger className="w-[70px] h-9" data-testid={testId ? `${testId}-hour` : undefined}>
          <SelectValue placeholder="Hr" />
        </SelectTrigger>
        <SelectContent>
          {hours.map(h => (
            <SelectItem key={h} value={h}>{h}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      <span className="text-muted-foreground font-medium">:</span>

      <Select
        value={minute}
        onValueChange={(m) => update(hour12 || '12', m, period)}
      >
        <SelectTrigger className="w-[70px] h-9" data-testid={testId ? `${testId}-min` : undefined}>
          <SelectValue placeholder="Min" />
        </SelectTrigger>
        <SelectContent>
          {minutes.map(m => (
            <SelectItem key={m} value={m}>{m}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={period}
        onValueChange={(p) => update(hour12 || '12', minute || '00', p)}
      >
        <SelectTrigger className="w-[72px] h-9" data-testid={testId ? `${testId}-period` : undefined}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="AM">AM</SelectItem>
          <SelectItem value="PM">PM</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
};
